/**
 * @file Walks one `UILayout` structure into the three target universes an
 * editor surface offers: header fields, entity-reference slots and ArTables.
 * Pure — the caller owns the subscriptions that produce `structure`,
 * `fieldTypes` and `entities`. Consumers: `useTargetFields` (mapping review)
 * and the Derived values pane's target select. Invariants: a reference slot is
 * keyed by `entityGroupId ?? entityId`, because that is the key
 * `FormVersion.entityReferences` and `FormDeriveReferencePropSource` both use;
 * repeated ids collapse to their first occurrence, so layout order decides
 * which duplicate's label survives.
 */
import { UIElementType } from '#types';
import type { Entity, FieldType, UIElement } from '#types';

export interface TargetFieldOption {
  id: string;
  label: string;
  required?: boolean;
  reference?: { entityId: string; entityGroupId?: string };
}

export interface TargetReferenceOption {
  slotKey: string;
  entityId: string;
  entityGroupId?: string;
  label: string;
  required?: boolean;
}

export interface TargetTableOption {
  tableId: string;
  label: string;
}

export interface LayoutUniverses {
  fields: TargetFieldOption[];
  references: TargetReferenceOption[];
  tables: TargetTableOption[];
}

function collectFields(
  element: UIElement | undefined,
  fieldTypes: Record<string, FieldType>,
): TargetFieldOption[] {
  if (!element) return [];
  if (element.uiElementType === UIElementType.Field) {
    return [
      {
        id: element.fieldId,
        label: fieldTypes[element.fieldId]?.description?.shortLabel ?? element.fieldId,
        ...(element.required || element.alwaysRequired ? { required: true } : {}),
      },
    ];
  }
  if ('children' in element) {
    return element.children.flatMap((child) => collectFields(child, fieldTypes));
  }
  return [];
}

function collectReferences(
  element: UIElement | undefined,
  entities: Record<string, Entity>,
): TargetReferenceOption[] {
  if (!element) return [];
  if (element.uiElementType === UIElementType.EntityReference && element.entityId) {
    const slotKey = element.entityGroupId ?? element.entityId;
    return [
      {
        slotKey,
        entityId: element.entityId,
        ...(element.entityGroupId ? { entityGroupId: element.entityGroupId } : {}),
        label: entities[element.entityId]?.description?.shortLabel ?? slotKey,
        ...(element.required || element.alwaysRequired ? { required: true } : {}),
      },
    ];
  }
  if ('children' in element) {
    return element.children.flatMap((child) => collectReferences(child, entities));
  }
  return [];
}

function collectTables(element: UIElement | undefined): TargetTableOption[] {
  if (!element) return [];
  if (element.uiElementType === UIElementType.Table) {
    return [{ tableId: element.tableId, label: element.tableId }];
  }
  if ('children' in element) {
    return element.children.flatMap((child) => collectTables(child));
  }
  return [];
}

function dedupe<T extends { id?: string; slotKey?: string; tableId?: string }>(items: T[]): T[] {
  const keyOf = (x: T): string => x.id ?? x.slotKey ?? x.tableId ?? '';
  return items.filter((item, i, arr) => arr.findIndex((x) => keyOf(x) === keyOf(item)) === i);
}

function layoutUniverses(
  structure: UIElement | undefined,
  fieldTypes: Record<string, FieldType>,
  entities: Record<string, Entity>,
): LayoutUniverses {
  return {
    fields: dedupe(collectFields(structure, fieldTypes)),
    references: dedupe(collectReferences(structure, entities)),
    tables: dedupe(collectTables(structure)),
  };
}

export { layoutUniverses };
